import { easternIsoDay } from '../data/taskRules.js';
import type { Role } from '../data/types.js';
import {
  creditedRequirement,
  partyOf,
  requirementsOf,
  windowDays,
  type Party,
  type Requirement,
  type RequirementSettings,
} from './requirements.js';

/** StoreAuditStatus Title suffix per clock ("101 — DM Facility Audit (Auditor)"). */
export const CLOCK_TITLE_SUFFIX: Record<Party, string> = { dm: '', auditor: ' (Auditor)', store: ' (Store team)' };

/**
 * Resetting a compliance clock after a submitted audit (spec §7 +
 * responsible parties) — pure, so the browser's sync (direct mode) and the
 * server (Phase 6.5: the server decides, from the audit row it reads
 * itself) apply exactly the same rule.
 */

/** Minimal AuditTemplates row: enough to pick the live version and its requirements. */
export interface ClockTemplateRow {
  id: number;
  name: string;
  auditType: string;
  version: number;
  status: string;
  /** Definition parses (an unreadable row is never the live version). */
  readable: boolean;
  settings: RequirementSettings;
}

/**
 * The row a completion is credited to: the live (highest Published,
 * readable) version of the audit's template family — same rule as
 * templateAdmin.liveTemplateFor — else the audit's own row. null = the
 * audit's own row is gone.
 */
export function liveVersionFor(rows: readonly ClockTemplateRow[], templateId: number): ClockTemplateRow | null {
  const own = rows.find((r) => r.id === templateId);
  if (!own) return null;
  const key = own.auditType.trim().toLowerCase();
  const live = rows
    .filter((r) => r.auditType.trim().toLowerCase() === key && r.status === 'Published' && r.readable)
    .sort((a, b) => b.version - a.version || b.id - a.id)[0];
  return live ?? own;
}

export interface StatusRowFields {
  Title: string;
  StoreId: number;
  TemplateId: number;
  LastCompletedDate: string;
  NextDueDate: string;
  WindowStatus: string;
  /**
   * Flow 1's transition bookkeeping, cleared by every credit: a new cycle
   * starts with no notice sent (Flow 1 itself re-stamps 'current' on its
   * next run — Recovered_to_current — so this only closes the gap until
   * then, when the app's "next step" would still read the old 'escalated';
   * whole-app review 2026-10-26). Absent on a re-credit of the SAME audit
   * (an idempotent retry is not a new cycle — blanking the stamp again would
   * re-arm Flow 1's "Audit due" and escalation mail; security review).
   */
  LastNotifiedStatus?: string;
  /** 'auditor' | 'store'; blank = DM. */
  Party?: string;
}

/**
 * The clock's next due date: the audit's EASTERN calendar day + the window,
 * at a fixed instant (12:00Z = 07:00/08:00 Eastern, always that same day).
 * The old `completedAt + N × 24 h` kept the submit time, so across a DST
 * change its Eastern day drifted by one against the board's day arithmetic
 * and the scorecard's rebuilt due days (Oct 25 00:30 EDT + 45 d = Dec 8
 * 23:30 EST — a 44-day window; whole-app review 2026-10-26). Every reader
 * (daysUntil, Flows 1/4 convertFromUtc, the reports) takes the Eastern day.
 */
export function nextDueOn(completedAt: string, days: number): string {
  const day = easternIsoDay(new Date(completedAt));
  const t = Date.parse(`${day}T12:00:00.000Z`) + days * 86_400_000;
  return new Date(t).toISOString();
}

export interface ExistingClockRow {
  id: string;
  party: unknown;
  lastCompleted: string | null | undefined;
}

export type ClockPlan =
  | { action: 'none'; reason: 'noRequirement' }
  | { action: 'none'; reason: 'newerExists'; party: Party }
  | { action: 'create'; fields: StatusRowFields; party: Party }
  | { action: 'update'; rowId: string; fields: StatusRowFields; party: Party };

export function planClockCredit(input: {
  storeId: number;
  storeNumber: string;
  /** The live version (liveVersionFor). */
  template: ClockTemplateRow;
  /** ISO time the audit was submitted. */
  completedAt: string;
  /** The conductor's REAL role (decides DM vs Auditor clock). */
  conductorRole: Role | undefined;
  /** The store is flagged Long distance (its DM/Auditor clocks may use the template's second window). */
  longDistance?: boolean;
  /** StoreAuditStatus rows for (store, live template), any party. */
  existing: readonly ExistingClockRow[];
}): ClockPlan {
  const credited = creditedRequirement(requirementsOf(input.template.settings), input.conductorRole);
  if (!credited) return { action: 'none', reason: 'noRequirement' };
  const nextDue = nextDueOn(input.completedAt, windowDays(credited, input.longDistance === true));
  const fields: StatusRowFields = {
    Title: `${input.storeNumber} — ${input.template.name}${CLOCK_TITLE_SUFFIX[credited.party]}`,
    StoreId: input.storeId,
    TemplateId: input.template.id,
    LastCompletedDate: input.completedAt,
    NextDueDate: nextDue,
    WindowStatus: 'current',
    LastNotifiedStatus: '',
    // DM rows leave Party blank (= DM), so DM-only templates never depend
    // on the Party column existing.
    ...(credited.party !== 'dm' ? { Party: credited.party } : {}),
  };
  // Normally one row per party; if a publish raced a sync and left two,
  // update the most recent (Admin → Templates "Re-check due dates" merges
  // them).
  const row = input.existing
    .filter((r) => partyOf(r.party) === credited.party)
    .sort((a, b) => (b.lastCompleted ?? '').localeCompare(a.lastCompleted ?? ''))[0];
  // `party` = whose clock this audit counted for — the server stamps it on
  // the Audits row (Audits.Party, owner ruling 2026-10-25) so reports can
  // tell a DM's audit from an Auditor's on a template that requires both.
  if (!row) return { action: 'create', fields, party: credited.party };
  // Any completed audit resets the clock; never move it backwards. Compared
  // as instants: SharePoint reads back whole seconds ('…:00Z') while the app
  // writes milliseconds ('…:00.000Z') — a text compare called the same
  // second "newer" (whole-app review 2026-10-26).
  // Floored to seconds: a direct-mode retry carries milliseconds the row lost.
  const seconds = (iso: string) => Math.floor(Date.parse(iso) / 1000);
  const last = row.lastCompleted ? seconds(row.lastCompleted) : NaN;
  if (Number.isFinite(last) && last > seconds(input.completedAt)) return { action: 'none', reason: 'newerExists', party: credited.party };
  if (Number.isFinite(last) && last === seconds(input.completedAt)) {
    // The same audit again (a retried sync, a repeated credit call): the clock already started this cycle.
    const { LastNotifiedStatus: _cleared, ...same } = fields;
    return { action: 'update', rowId: row.id, fields: same, party: credited.party };
  }
  return { action: 'update', rowId: row.id, fields, party: credited.party };
}

/** A store's clock as read for a long-distance recompute. */
export interface RecomputeClockRow {
  id: string;
  templateId: number;
  party: unknown;
  lastCompleted: string | null | undefined;
  nextDue: string | null | undefined;
}

/**
 * Turning a store's Long distance flag on or off applies RIGHT AWAY (owner
 * ruling 2026-10-20): each of its clocks gets next due = last completed +
 * the window that now applies (live template, the clock's party). Clocks of
 * templates no longer live, parties no longer required, or never completed
 * are left alone, and so are clocks whose window the flag doesn't change
 * (store team; no long-distance window); unchanged dates aren't rewritten. Pure.
 */
export function planLongDistanceRecompute(input: {
  clocks: readonly RecomputeClockRow[];
  /** Live template rows (the clocks point at live versions after a publish). */
  templates: readonly { id: number; requirements: readonly Requirement[] }[];
  longDistance: boolean;
}): { rowId: string; nextDue: string }[] {
  const out: { rowId: string; nextDue: string }[] = [];
  for (const c of input.clocks) {
    const template = input.templates.find((t) => t.id === c.templateId);
    const last = c.lastCompleted ? new Date(c.lastCompleted) : null;
    if (!template || !last || Number.isNaN(last.getTime())) continue;
    const party = partyOf(c.party);
    const req = template.requirements.find((r) => r.party === party);
    if (!req) continue;
    // Only clocks the flag actually changes: store-team clocks and templates
    // without a long-distance window keep their dates (a frequency changed on
    // publish still applies from the next audit — QA review 2026-10-20).
    if (windowDays(req, true) === windowDays(req, false)) continue;
    const nextDue = nextDueOn(last.toISOString(), windowDays(req, input.longDistance));
    const current = c.nextDue ? new Date(c.nextDue).getTime() : NaN;
    // SharePoint keeps whole seconds: within a minute = already right.
    if (Number.isFinite(current) && Math.abs(current - new Date(nextDue).getTime()) < 60_000) continue;
    out.push({ rowId: c.id, nextDue });
  }
  return out;
}
